import { SlidersHorizontal, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { DENSITIES, defaultCollectionCfg } from "@/lib/collections";

// Reusable "Display" control (ContentOS Collection Standard): density +
// which optional properties are visible and their order. The collection
// definition supplies the property list; `locked` properties can't be hidden.
export function CollectionControls({ def, cfg, update }) {
  const props = def.properties || [];
  const byKey = Object.fromEntries(props.map((p) => [p.key, p]));
  const hidden = new Set(cfg.hidden || []);
  const order = (cfg.order && cfg.order.length ? cfg.order : props.map((p) => p.key)).filter((k) => byKey[k]);

  const toggle = (key) => {
    if (byKey[key]?.locked) return;
    const next = new Set(hidden);
    next.has(key) ? next.delete(key) : next.add(key);
    update({ hidden: [...next] });
  };
  const move = (key, dir) => {
    const i = order.indexOf(key), j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order]; [next[i], next[j]] = [next[j], next[i]];
    update({ order: next });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="collection-display-btn">
          <SlidersHorizontal className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Display</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0" data-testid="collection-display-panel">
        <div className="border-b border-border/60 p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Density</p>
          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
            {DENSITIES.map(([k, label]) => (
              <button key={k} onClick={() => update({ density: k })} data-testid={`collection-density-${k}`}
                className={cn("rounded-md py-1.5 text-xs font-medium transition-colors", cfg.density === k ? "bg-background text-foreground shadow-sm" : "text-muted-foreground")}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Properties</p>
          <div className="space-y-0.5">
            {order.map((key) => {
              const def2 = byKey[key]; if (!def2) return null;
              const Icon = def2.icon; const on = !hidden.has(key); const locked = !!def2.locked;
              return (
                <div key={key} className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/50" data-testid={`collection-prop-row-${key}`}>
                  <button onClick={() => toggle(key)} disabled={locked} data-testid={`collection-prop-toggle-${key}`}
                    className={cn("flex flex-1 items-center gap-2 text-left text-xs", on ? "text-foreground" : "text-muted-foreground/60", locked && "cursor-default")}>
                    <span className={cn("flex h-4 w-4 items-center justify-center rounded border", on ? "border-accent bg-accent text-accent-foreground" : "border-border")}>
                      {on && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                    </span>
                    {Icon && <Icon className="h-3.5 w-3.5" />} {def2.label}{locked && <span className="ml-1 text-[10px] text-muted-foreground/60">(always)</span>}
                  </button>
                  <button onClick={() => move(key, -1)} data-testid={`collection-prop-up-${key}`} className="rounded p-0.5 text-muted-foreground/60 hover:bg-muted hover:text-foreground"><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => move(key, 1)} data-testid={`collection-prop-down-${key}`} className="rounded p-0.5 text-muted-foreground/60 hover:bg-muted hover:text-foreground"><ChevronDown className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end border-t border-border/60 p-2">
          <Button variant="ghost" size="sm" onClick={() => update(defaultCollectionCfg(def))} data-testid="collection-display-reset">
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset to default
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
